import { useMutation, useQuery, useQueryClient } from "react-query";
import { ApproveDayPass, CancelDayPass, CheckInDayPass, CheckOutDayPass, CreateDayPass, ExtendDayPass, GetDayPassById, GetDayPassesByCustomerEmail, GetDayPassesByCustomerId, GetDayPassesByDate, UpdateDayPassPaymentStatus } from "../services/backend";
import { Notify, NotifyError } from "../utils/functions";



export const useCreateDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(CreateDayPass, {
        onSuccess: () => {
            Notify("Day pass added successfully");
            queryClient.invalidateQueries(['daypasses']);
            queryClient.invalidateQueries(['daypasses-by-customer-id']);
            queryClient.invalidateQueries(['daypasses-by-customer-email']);
        },
        onError: (error) => {
            NotifyError("Failed to add day pass", error);
            console.error("Error adding day pass:", error);
        }
    });
};

export const useGetDayPassesByCustomerId = (customerId) => {
    return useQuery(['daypasses-by-customer-id', customerId], () => GetDayPassesByCustomerId(customerId), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to get day passes by customer id", error);
            console.error("Error getting day passes by customer id:", error);
        }
    });
};

export const useGetDayPassesByCustomerEmail = (customerEmail) => {
    return useQuery(['daypasses-by-customer-email'], () => GetDayPassesByCustomerEmail(customerEmail), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to get day passes by customer id", error);
            console.error("Error getting day passes by customer id:", error);
        }
    });
};

export const useApproveDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => ApproveDayPass(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Day pass approved successfully");
                // Invalidate multiple related queries
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
                queryClient.invalidateQueries(['daypasses-by-customer-email']);
                queryClient.invalidateQueries(['daypasses-by-date']);
            },
            onError: (error) => {
                NotifyError("Failed to approve day pass", error);
                console.error("Error approving day pass:", error);
            }
        }
    );
};

export const useCheckInDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => CheckInDayPass(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Customer checked in successfully");
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
                queryClient.invalidateQueries(['daypasses-by-date']);
            },
            onError: (error) => {
                NotifyError("Failed to check in day pass", error);
                console.error("Error checking in day pass:", error);
            }
        }
    );
};

export const useCheckOutDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => CheckOutDayPass(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Customer checked out successfully");
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
                queryClient.invalidateQueries(['daypasses-by-date']);
            },
            onError: (error) => {
                NotifyError("Failed to check out day pass", error);
                console.error("Error checking out day pass:", error);
            }
        }
    );
};

export const useUpdateDayPassPayment = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => UpdateDayPassPaymentStatus(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Day pass payment updated successfully");
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
            },
            onError: (error) => {
                NotifyError("Failed to update day pass payment", error);
                console.error("Error updating day pass payment:", error);
            }
        }
    );
};

// Get Day Pass by ID
export const useGetDayPassById = (dayPassId, options = {}) => {
    return useQuery(
        ['daypass', dayPassId],
        () => GetDayPassById(dayPassId),
        {
            enabled: !!dayPassId,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            select: (data) => {
                return data?.data?.dayPass || {};
            },
            onError: (error) => {
                NotifyError("Failed to fetch day pass details", error);
                console.error("Error fetching day pass:", error);
            },
            ...options
        }
    );
};

// Get Day Passes by Date
export const useGetDayPassesByDate = (date, options = {}) => {
    return useQuery(
        ['daypasses-by-date', date],
        () => GetDayPassesByDate(date),
        {
            enabled: !!date,
            refetchOnWindowFocus: false,
            staleTime: 2 * 60 * 1000, // 2 minutes for date-specific queries
            select: (data) => {
                return data?.data || [];
            },
            onError: (error) => {
                NotifyError("Failed to get day passes by date", error);
                console.error("Error getting day passes by date:", error);
            },
            ...options
        }
    );
};

// Cancel Day Pass
export const useCancelDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => CancelDayPass(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Day pass cancelled successfully");
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
                queryClient.invalidateQueries(['daypasses-by-date']);
            },
            onError: (error) => {
                NotifyError("Failed to cancel day pass", error);
                console.error("Error cancelling day pass:", error);
            }
        }
    );
};

// Extend Day Pass
export const useExtendDayPass = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ dayPassId, data }) => ExtendDayPass(dayPassId, data),
        {
            onSuccess: (data, variables) => {
                Notify("Day pass extended successfully");
                queryClient.invalidateQueries(['daypasses']);
                queryClient.invalidateQueries(['daypass', variables.dayPassId]);
                queryClient.invalidateQueries(['daypasses-by-customer-id']);
                queryClient.invalidateQueries(['daypasses-by-date']);
            },
            onError: (error) => {
                NotifyError("Failed to extend day pass", error);
                console.error("Error extending day pass:", error);
            }
        }
    );
};
